"use client";

import { Typography, cn, getCSSVar, useKeydown } from "@tosize/ui";
import { AnimatePresence, motion } from "motion/react";
import { useRouter } from "next/navigation";
import { Slot } from "radix-ui";
import { type ReactNode, useEffect, useRef, useState } from "react";
import { usePathnameChange } from "~/hooks/useRouteChange";

interface Props {
	title?: string;
	content: ReactNode;
	children: ReactNode;
	className?: string;
	wrapperClassName?: string;
	variant?: "default" | "light";
	width?: "default" | "small" | "wide";
	route?: string;
}

export function DropdownButton(props: Props) {
	const {
		title,
		content,
		children,
		className,
		wrapperClassName,
		variant,
		width = "default",
		route,
	} = props;
	const router = useRouter();
	const [open, setOpen] = useState(false);
	const ref = useRef<HTMLDivElement>(null);

	useEffect(() => {
		const handleClick = (event: MouseEvent) => {
			if (
				event.target instanceof Element &&
				ref.current &&
				!ref.current.contains(event.target) &&
				getCSSVar("--dialog-open") !== 1
			) {
				setOpen(false);
			}
		};

		document.addEventListener("click", handleClick);

		return () => {
			document.removeEventListener("click", handleClick);
		};
	}, []);

	useKeydown(() => setOpen(false));
	usePathnameChange(() => setOpen(false));

	return (
		<div className={cn("relative", wrapperClassName)} ref={ref}>
			<Slot.Root
				onClick={(e) => {
					if (e.metaKey && !!route) {
						router.push(route);
					} else {
						setOpen(!open);
					}
				}}
			>
				{children}
			</Slot.Root>

			<AnimatePresence>
				{open && (
					<motion.div
						key="dropdown"
						className={cn(
							"before:-top-2 absolute top-[calc(100%+1rem)] right-0 flex flex-col gap-1 whitespace-nowrap text-wrap rounded-b px-4 pt-1 pb-3 shadow before:absolute before:right-0 before:left-0 before:h-2",
							{
								"bg-shades-light before:bg-shades-light": variant !== "light",
								"bg-white before:bg-white": variant === "light",
								"min-w-52": width === "small",
								"min-w-60": width === "default",
								"min-w-96": width === "wide",
							},
							className,
						)}
						initial={{ scaleY: 0, originY: 0 }}
						animate={{ scaleY: "100%", originY: 0 }}
						transition={{ duration: 0.15, type: "tween" }}
					>
						{!!title && (
							<Typography size="text-sm" color="text-shades-dark">
								{title}
							</Typography>
						)}

						{content}
					</motion.div>
				)}
			</AnimatePresence>
		</div>
	);
}
